import { useTranslation } from "react-i18next";
import { useNavigate, useOutletContext } from "react-router-dom";
import React, { useEffect, useState } from "react";
import { RiderProfileLayoutContextInterface } from "../RiderProfileLayout";
import ImageInput from "../../../Components/ImageInput";
import { patrollerService } from "../../../Api/Services/PatrollerService";
import { useLoading } from "../../../Context/LoadingProvider";
import RadioBtn from "../../../Components/RadioBtn";

const IdVerificationStep: React.FunctionComponent<any> = ({
                                                              setStep,
                                                              setProgressStep,
                                                              needsVehicleCheck,
                                                              continueAfterFakeId,
                                                          }) => {
    const navigate = useNavigate();
    const { t } = useTranslation();
    const { rider, setSubTitle } =
        useOutletContext<RiderProfileLayoutContextInterface>();
    const [isFakeId, setIsFakeId] = useState<boolean | undefined>(undefined);
    const [idImage, setIdImage] = useState<File[]>([]);
    const { startLoading, finnishLoading } = useLoading();
    const [displayFakeIdMessage, setDisplayFakeIdMessage] =
        useState<boolean>(false);

    // 🔹 új state a megjegyzéshez és a képekhez
    const [note, setNote] = useState("");
    const [noteImages, setNoteImages] = useState<File[]>([]);

    useEffect(() => {
        setSubTitle();
    }, [t, setSubTitle]);

    const handleNext = () => {
        if (isFakeId === undefined) return;
        if (isFakeId && idImage.length === 0) return;

        startLoading("id-verification");
        patrollerService
            .saveIdStepCheckForRider(rider.riderId, !isFakeId, idImage[0] ?? undefined)
            ?.then(() => {
                if (isFakeId) {
                    if (continueAfterFakeId === false) {
                        setDisplayFakeIdMessage(true);
                        return;
                    }
                }

                if (needsVehicleCheck >= 0) {
                    setStep((prev: number) => prev + 1);
                    setProgressStep((prev: number) => prev + 1);
                    return;
                }

                setStep(1);
                setProgressStep(1);
            })
            .finally(() => finnishLoading("id-verification"));
    };

    setSubTitle(t("Does the rider have a fake ID?"));

    return (
        <>
            {!displayFakeIdMessage ? (
                <>
                    <div className="mb-1 heading">
                        {t("Does the rider have a fake ID?")}
                    </div>

                    <div className="text-light mb-4">
                        {t("Please choose an option!")}
                    </div>

                    {rider.idCardNumber && (
                        <div className="license-plate mb-4">{rider.idCardNumber}</div>
                    )}

                    <div className="flex items-center gap-4 mb-2">
                        <RadioBtn
                            label={t("Yes")}
                            selected={isFakeId === true}
                            onSelect={() => setIsFakeId(true)}
                            size="large"
                        />

                        <RadioBtn
                            label={t("No")}
                            selected={isFakeId === false}
                            onSelect={() => setIsFakeId(false)}
                            size="large"
                        />
                    </div>

                    {isFakeId === true && (
                        <>
                            <div className="separator-line mb-2"></div>
                            <ImageInput
                                images={idImage}
                                setImages={setIdImage}
                                title={t("Take a photo")}
                                infoText={t("Confirmation photo")}
                                multiple={false}
                                required={false}
                                className="mb-4"
                            />
                        </>
                    )}

                    <button
                        type="submit"
                        className="button mt-8"
                        onClick={() => handleNext()}
                        disabled={isFakeId === undefined}
                    >
                        {t("Next")}
                    </button>
                </>
            ) : (
                <div className="card bg-white rounded-xl p-6 shadow-md">
                    <div className="mb-3 heading text-center text-red-600">
                        {t("Add a note about the fake ID")}
                    </div>

                    <textarea
                        className="input-field mb-4"
                        rows={4}
                        placeholder={t("Write your note here...") as string}
                        value={note}
                        onChange={(e) => setNote(e.target.value)}
                    />

                    <ImageInput
                        images={noteImages}
                        setImages={setNoteImages}
                        title={t("Add pictures")}
                        infoText={t("Attach confirmation photos if needed")}
                        multiple={true}
                        required={false}
                        className="mb-4"
                    />

                    <div className="flex gap-4 mt-8">
                        <div className="flex-1">
                        <button
                            className="button button--light"
                            onClick={() => {
                                setDisplayFakeIdMessage(false);
                                setNote("");
                                setNoteImages([]);
                            }}
                        >
                            {t("Back")}
                        </button>
                        </div>
                        <div className="flex-1">

                        <button
                            className="button"
                            onClick={async () => {
                                startLoading("fake-id-note");
                                try {
                                    await patrollerService.saveFakeIdNote(
                                        rider.riderId,
                                        note,
                                        noteImages
                                    );
                                    await patrollerService.finishCheckForRider(
                                        rider.riderId
                                    );
                                    setTimeout(() => navigate("/"), 800);
                                } finally {
                                    finnishLoading("fake-id-note");
                                }
                            }}
                            disabled={!note && noteImages.length === 0}
                        >
                            {t("Save & Continue")}
                        </button>
                        </div>
                    </div>
                </div>
            )}
        </>
    );
};

export default IdVerificationStep;
